import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Wand2,
  Upload,
  Sparkles,
  Download,
  X,
  ZoomIn,
  Eraser,
  Palette,
  Crown,
  Image as ImageIcon,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const ImageEnhancer = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('upscale');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [enhancedImage, setEnhancedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const getCreditsRemaining = () => {
    if (!user) return 0;
    return Math.max(0, (user.creditsLimit || 0) - (user.creditsUsed || 0));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload a valid image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large (max 10MB)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImage(reader.result as string);
      setEnhancedImage(null);
    };
    reader.readAsDataURL(file);
  };

  const handleEnhance = async (type: string) => {
    if (!uploadedImage) {
      toast.error('Please upload an image first');
      return;
    }

    if (getCreditsRemaining() < 1) {
      toast.error('You need at least 1 credit for image enhancement');
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 300);

    try {
      const enhancementPrompts: Record<string, string> = {
        upscale: 'Enhance this image: increase resolution, add sharper details, improve clarity while maintaining the original style and content. Make it 4K quality.',
        background: 'Remove the background from this image completely, making it transparent. Keep only the main subject with clean edges.',
        enhance: 'Improve this image: better lighting, enhanced colors, noise reduction, sharper details. Make it look professional and high-quality.',
        colorize: 'If this is a black and white image, colorize it with natural, realistic colors. If already colored, enhance and optimize the color palette.'
      };

      const { data, error } = await supabase.functions.invoke('generate-image', {
        body: {
          prompt: enhancementPrompts[type],
          uploadedImage: uploadedImage,
          resolution: '1024x1024',
          style: 'photographic',
          realistic: true
        }
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (error) {
        throw new Error(error.message);
      }

      if (data?.success && data?.imageUrl) {
        setEnhancedImage(data.imageUrl);
        toast.success('Image enhanced successfully!');
      } else {
        throw new Error('Enhancement failed');
      }
    } catch (error) {
      console.error('Enhancement error:', error);
      toast.error('Failed to enhance image. Please try again.');
    } finally {
      clearInterval(progressInterval);
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const enhancementTypes = [
    {
      id: 'upscale',
      title: 'AI Upscale',
      description: 'Increase resolution up to 4x',
      icon: ZoomIn,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'background',
      title: 'Remove Background',
      description: 'Clean transparent background',
      icon: Eraser,
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'enhance',
      title: 'Auto Enhance',
      description: 'Improve lighting & quality',
      icon: Sparkles,
      gradient: 'from-amber-500 to-orange-500'
    },
    {
      id: 'colorize',
      title: 'AI Colorize',
      description: 'Add color to B&W photos',
      icon: Palette,
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden bg-card">
      <CardHeader className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 sm:p-8">
        <CardTitle className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
            <Wand2 className="w-6 h-6 text-white" />
          </div>
          <div className="text-white flex-1">
            <h3 className="text-xl sm:text-2xl font-bold">AI Image Enhancer</h3>
            <p className="text-white/80 text-sm font-normal mt-1">
              Upscale, remove backgrounds, enhance quality
            </p>
          </div>
          <Badge className="bg-white/20 text-white border-0">
            1 Credit
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-6 sm:p-8 space-y-6">
        {/* Credits Notice */}
        <div className="flex items-center justify-center p-4 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-2xl">
          <span className="text-lg mr-2">✨</span>
          <p className="text-indigo-700 dark:text-indigo-300 text-sm sm:text-base font-medium">
            You have <span className="font-bold">{getCreditsRemaining()}</span> credits remaining.
          </p>
        </div>

        {/* Upload Section */}
        <div className="space-y-4">
          <Label className="text-base font-semibold flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload Image to Enhance
          </Label>
          
          {!uploadedImage ? (
            <div className="relative">
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
              />
              <label 
                htmlFor="image-upload"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-border hover:border-primary rounded-2xl cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <Upload className="w-10 h-10 text-muted-foreground mb-3" />
                <span className="text-muted-foreground font-medium">Click to upload image</span>
                <span className="text-xs text-muted-foreground mt-1">PNG, JPG up to 10MB</span>
              </label>
            </div>
          ) : (
            <div className="relative">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Original Image */}
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Original</Label>
                  <div className="relative rounded-2xl overflow-hidden border-2 border-border">
                    <img 
                      src={uploadedImage} 
                      alt="Original" 
                      className="w-full h-48 object-cover"
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => {
                        setUploadedImage(null);
                        setEnhancedImage(null);
                      }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Enhanced Image */}
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Enhanced</Label>
                  <div className="relative rounded-2xl overflow-hidden border-2 border-border bg-muted/30 h-48 flex items-center justify-center">
                    {enhancedImage ? (
                      <img 
                        src={enhancedImage} 
                        alt="Enhanced" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-muted-foreground">
                        <ArrowRight className="w-8 h-8 mb-2" />
                        <span className="text-sm">Enhanced result will appear here</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Enhancement Options */}
        {uploadedImage && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">Choose Enhancement</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {enhancementTypes.map((type) => (
                <Button
                  key={type.id}
                  variant="outline"
                  onClick={() => handleEnhance(type.id)}
                  disabled={isProcessing}
                  className={`h-auto py-4 flex flex-col items-center gap-2 border-2 hover:border-primary transition-all ${
                    activeTab === type.id ? 'border-primary bg-primary/5' : ''
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${type.gradient} flex items-center justify-center`}>
                    <type.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-semibold text-sm">{type.title}</span>
                  <span className="text-xs text-muted-foreground">{type.description}</span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Progress */}
        {isProcessing && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Processing image...</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Download Button */}
        {enhancedImage && (
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  const response = await fetch(enhancedImage);
                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `genova-enhanced-${Date.now()}.png`;
                  link.click();
                  URL.revokeObjectURL(url);
                  toast.success('Image downloaded!');
                } catch (error) {
                  toast.error('Download failed');
                }
              }}
              className="px-6 py-3 font-semibold rounded-xl border-2"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Enhanced Image
            </Button>
            
            {user?.plan === 'free' && (
              <Button 
                className="px-6 py-3 font-semibold rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600"
                onClick={() => window.location.href = '/pricing'}
              >
                <Crown className="w-5 h-5 mr-2" />
                Unlock HD Quality
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageEnhancer;
